// V14.13 — Guest-link password gate.
// Shown before MPGuestPublicDisplayPage renders when the backend returns
// 401 `guest_password_required` for the exercise. Exchange the password
// for a short HMAC pass token (12h), store it in sessionStorage, then
// reload the page so the normal flow re-runs with the X-Guest-Pass header.
import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Lock } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const passKey = (guestToken) => `mp_guest_pass_${guestToken}`;

export const getGuestPass = (guestToken) => {
  try { return sessionStorage.getItem(passKey(guestToken)) || ''; } catch { return ''; }
};

const GuestPasswordGate = ({ guestToken, onUnlocked, exerciseName }) => {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/mp/guest/verify-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ guest_token: guestToken, password }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || 'Incorrect password');
      }
      const { pass_token } = await res.json();
      try { sessionStorage.setItem(passKey(guestToken), pass_token); } catch { /* noop */ }
      onUnlocked();
    } catch (err) {
      toast.error(err.message || 'Incorrect password');
      setPassword('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4" data-testid="guest-password-gate">
      <div className="w-full max-w-md bg-card border border-border rounded-md p-8 space-y-6">
        <div className="text-center space-y-2">
          <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Lock className="w-6 h-6 text-primary" />
          </div>
          <h1 className="font-heading font-bold text-xl uppercase tracking-tight text-foreground">
            {exerciseName || 'Media Portal'}
          </h1>
          <p className="text-sm text-muted-foreground">This guest link is password protected. Enter the password to continue.</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="guest-pw" className="text-xs uppercase tracking-wider">Password</Label>
            <Input
              id="guest-pw"
              data-testid="guest-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              required
              className="rounded-sm"
            />
          </div>
          <Button
            type="submit"
            disabled={loading || !password}
            data-testid="guest-password-submit"
            className="w-full rounded-sm"
          >
            {loading ? 'Verifying…' : 'Continue'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default GuestPasswordGate;
